//Components
import Module from '../../../../layout/Module'
import Box from '@mui/material/Box'
import Skeleton from '@mui/material/Skeleton'
import InputDateUTC from '../../../../FormControl/input/InputDateUTC'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import DialogConfirmCancel from '../../../../dialog/DialogConfirm'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import TableStudents from './TableStudents'
import AutocompleteFieldTemplateAssessment from '../../../../FormControl/AutocompleteFieldRequest/AutocompleteFieldTemplateAssessment'
import AlertSaveButton from '../../../../button/AlertSaveButton'
import SelectClasses from './SelectClasses'
import GlobalReviewers from './GlobalReviewers/GlobalReviewers'
import RoundsBuilder from './RoundsBuilder'
import isEmpty from 'lodash/isEmpty'
import ButtonStartIcon from '../../../../button/ButtonStartIcon'
import { FormControlLabel, Radio } from '@mui/material'
import useAssessment from '../../../../../hooks/data-entry/useAssessment'

//Utils
import cleanAsessment from '../../helpers/assessmentCleaner'
import {
  createAssessment,
  updateAssessment,
  getAssessmentNameTaken
} from '../../../../../services/data-entry/assessments.service'
import { debounce } from '../../../../../utils/debounce'

//Other dependencies
import { updateSkip } from '../../../../../store/actions/datatable.actions'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useDispatch, useSelector } from 'react-redux'
import { useRef } from 'react'
import { toggleAlertModule, alertReset } from '../../../../../store/actions/alert.actions'
import { updateFilter } from '../../../../../store/actions/filter.actions'
import {
  updateAssessmentsBuilderTemplate,
  updateAssessmentsBuilderFilteredStudents,
  updateAssessmentsBuilderClass,
  updateAssessmentBuilder,
  resetAssesmentBuilder,
  checkedStudentsIds
} from '../../../../../store/actions/dataEntryAssessmentBuilderActions'
import { ASSESSMENT_TYPE_STANDARD, ASSESSMENT_TYPE_CUSTOM } from '../../constants/typeAssessment'

dayjs.extend(utc)
dayjs.extend(timezone)

const AssessmentBuilder = ({ mode = 'adding' }) => {
  const currentModule = 'dataentryassessment'
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const saveRef = useRef(null)
  const isEditing = mode === 'editing'
  const { assessmentId, type } = useParams()
  const assessmentType = [ASSESSMENT_TYPE_STANDARD, ASSESSMENT_TYPE_CUSTOM].includes(type)
    ? type
    : ASSESSMENT_TYPE_STANDARD // Default to standard if type is not recognized
  const { getFullAssessmentData } = useAssessment(assessmentType)
  const alertSave = useSelector((state) => state?.alert?.save || false)
  const permissions = useSelector((state) => state?.session?.permissions?.dataentryassessment ?? {})

  const assessmentsBuilder = useSelector(
    (state) => state?.dataEntryAssessmentBuilder?.assessmentsBuilder ?? {}
  )

  const selectedTemplate = useSelector(
    (state) => state?.dataEntryAssessmentBuilder?.assessmentsBuilder?.template ?? null
  )

  const loading = useSelector(
    (state) =>
      state?.dataEntryAssessmentBuilder?.assessmentsBuilder?.studentsListLoading ||
      state?.dataEntryAssessmentBuilder?.assessmentsBuilder?.classesListLoading ||
      false
  )

  const checkedIds = useSelector(
    (state) => state?.dataEntryAssessmentBuilder?.assessmentsBuilder?.checkedIds ?? []
  )

  const participantRoles = useSelector(
    (state) => state?.dataEntryAssessmentBuilder?.assessmentsBuilder?.participantRoles ?? ['Student']
  )
  const studentsWithMarking = useSelector(
    (state) => state?.dataEntryAssessmentBuilder?.assessmentsBuilder?.studentsWithMarking ?? []
  )

  const studentsWithMarkingIds = studentsWithMarking?.map((student) => student._id)
  const { class: classData, name, error, template, sittingDate, dueDate, rounds } = assessmentsBuilder
  const hasRounds = Array.isArray(rounds) && rounds.length > 0
  const { enqueueSnackbar } = useSnackbar()
  const [open, setOpen] = useState(false)
  const [openConfirmModal, setOpenConfirmModal] = useState(false)
  const [openConfirmNoReviewersModal, setOpenConfirmNoReviewersModal] = useState(false)
  const [noReviewersMessage, setNoReviewersMessage] = useState(false)
  const [loadingSave, setLoadingSave] = useState(false)
  const [assessmentNameTaken, setAssessmentNameTaken] = useState('')

  // Check assessment name taken debounced
  const debouncedCheckAssessmentName = useRef(
    debounce(async (assessmentName, id) => {
      try {
        const res = await getAssessmentNameTaken(assessmentName, id)

        if (res?.data?.found) {
          setAssessmentNameTaken('This assessment name is already taken')
        } else {
          setAssessmentNameTaken('')
        }
      } catch (error) {
        setAssessmentNameTaken('')
      }
    }, 300)
  )

  const onChecked = (ids) => {
    if (error) error['students'] = null
    dispatch(updateAssessmentBuilder({ error }))
    dispatch(checkedStudentsIds(ids))
  }

  const onChangeParticipantType = (roles) => {
    dispatch(checkedStudentsIds([]))
    dispatch(updateAssessmentBuilder({ participantRoles: roles }))
  }

  const timezone = useSelector((state) => state.tenant?.timezone || 'Australia/Victoria')

  const onAlert = (show, value, onSave, onDontSave) => {
    dispatch(toggleAlertModule(show, 'assessments', value, onSave, onDontSave))
  }

  const onChangeAlert = () => {
    if (alertSave && !alertSave.mode) {
      onAlert(false, mode, () => {
        if (saveRef?.current) {
          saveRef.current.click()
        }
      })
    }
  }

  const onChangeTemplate = (template) => {
    if (String(template?._id) !== String(selectedTemplate?._id)) {
      dispatch(updateAssessmentsBuilderClass({ filteredClasses: [], selectedClasses: [] }))
      dispatch(updateAssessmentsBuilderFilteredStudents([]))
      onChecked([])
      dispatch(updateFilter('assessmentStudentsList', {}))
      dispatch(updateSkip('assessmentStudentsList', 0))
    }

    dispatch(updateAssessmentsBuilderTemplate(template))
    onChangeAlert()
  }

  const validateForm = () => {
    dispatch(updateAssessmentBuilder({ error: {} }))

    let error = {}

    if (!template) {
      error.template = 'Please choose a template'
    }

    if (assessmentType === ASSESSMENT_TYPE_STANDARD && (!classData || !classData?.selectedClasses?.length)) {
      error.class = 'Please choose classes'
    }

    if (!name || !name.trim().length) {
      error.name = 'Please enter assessment name'
    }

    // MF-399: con rounds, cada round trae sus fechas → las globales no se exigen.
    if (!hasRounds && !sittingDate) {
      error.sittingDate = 'Please enter assessment sitting date'
    }

    if (!hasRounds && !dueDate) {
      error.dueDate = 'Please enter assessment due date'
    }

    // MF-503: en STANDARD ("by classes") no hay selección granular — van todos los
    // alumnos de las clases (el backend los arma desde el roster). Solo CUSTOM exige
    // seleccionar participantes.
    if (
      assessmentType === ASSESSMENT_TYPE_CUSTOM &&
      (!checkedIds || !Array.isArray(checkedIds) || !checkedIds.length)
    ) {
      const participantLabel = participantRoles.includes('Teacher') ? 'teachers' : 'students'
      error['students'] = `Please select ${participantLabel} to assess`
    }

    if (!isEmpty(error)) {
      dispatch(
        updateAssessmentBuilder({
          error
        })
      )
      return false
    }

    return true
  }

  // Persist the assessment (create or update). Returns true when it succeeds and
  // the user is navigated away; false when it fails (a snackbar is shown).
  const persistAssessment = async () => {
    const payload = cleanAsessment(assessmentsBuilder)
    const response =
      mode === 'adding'
        ? await createAssessment({ ...payload, type: assessmentType })
        : await updateAssessment(assessmentId, { ...payload, type: assessmentType })

    if (response?.error || !response?.data) {
      enqueueSnackbar(`We could not ${mode == 'adding' ? 'create' : 'update'} the assessment`, {
        variant: 'error'
      })
      return false
    }

    dispatch(resetAssesmentBuilder())
    dispatch(alertReset())
    navigate('/dashboard/data-entry/assessments')
    return true
  }

  const onConfirmUpdate = async () => {
    try {
      setLoadingSave(true)
      const ok = await persistAssessment()
      if (!ok) setOpenConfirmModal(false)
    } catch (error) {
      enqueueSnackbar(`We could not update the assessment`, { variant: 'error' })
      setOpenConfirmModal(false)
    } finally {
      setLoadingSave(false)
    }
  }

  const hasMissingReviewers = (payload) => {
    const { classes = [], reviewers = [] } = payload

    // Handle the non-standard case first for an early exit
    if (assessmentType !== ASSESSMENT_TYPE_STANDARD) {
      return reviewers?.length === 0
    }

    const isMissing = classes.some((classItem) => {
      // Check if 'reviewers' is null/undefined OR an empty array
      return !classItem?.reviewers || classItem?.reviewers?.length === 0
    })

    return isMissing
  }

  const saveChanges = async () => {
    try {
      setLoadingSave(true)
      //if students already assessed are removed from the assessment show a modal
      if (
        mode === 'editing' &&
        studentsWithMarkingIds?.length > 0 &&
        !studentsWithMarkingIds?.every((id) => checkedIds?.includes(id))
      ) {
        // Hand off to the "removed students" confirmation modal.
        setOpenConfirmNoReviewersModal(false)
        setOpenConfirmModal(true)
        return
      }

      const ok = await persistAssessment()
      // On failure close the reviewer prompt so the user is back on the form
      // (the snackbar already explains what happened); on success we navigate away.
      if (!ok) setOpenConfirmNoReviewersModal(false)
    } catch (error) {
      enqueueSnackbar(`We could not ${mode == 'adding' ? 'create' : 'update'} the assessment`, {
        variant: 'error'
      })
      setOpenConfirmNoReviewersModal(false)
    } finally {
      setLoadingSave(false)
    }
  }

  const handleOnSaveAssessment = async () => {
    try {
      const isFormValid = validateForm()

      if (isFormValid) {
        const { data: assessmentNameCheck } = await getAssessmentNameTaken(
          name,
          isEditing ? assessmentId : ''
        )

        if (assessmentNameCheck?.found) {
          setAssessmentNameTaken('This assessment name is already taken')
          return
        } else {
          setAssessmentNameTaken('')
          const payload = cleanAsessment(assessmentsBuilder)
          const hasMissing = await hasMissingReviewers(payload)

          if (hasMissing) {
            const reviewerErrorMessage =
              assessmentType === ASSESSMENT_TYPE_CUSTOM
                ? 'No reviewers have been assigned for this assessment. Do you want to save the changes?'
                : 'One or more classes do not have a reviewer assigned. Do you want to save the changes?'

            setNoReviewersMessage(reviewerErrorMessage)
            setOpenConfirmNoReviewersModal(true)
            return
          } else {
            await saveChanges()
          }
        }
      }
    } catch (error) {
      enqueueSnackbar(`We could not ${mode == 'adding' ? 'create' : 'update'} the assessment`, {
        variant: 'error'
      })
    } finally {
      setLoadingSave(false)
    }
  }

  const onChangeHeader = (name, value) => {
    if (error) error[name] = null
    dispatch(updateAssessmentBuilder({ [name]: value, error }))
    if (name === 'name' && value && value.trim().length) {
      debouncedCheckAssessmentName.current(value, isEditing ? assessmentId : '')
    }
    onChangeAlert()
  }

  const onCancel = () => {
    dispatch(updateFilter('assessmentStudentsList', {}))
    dispatch(updateSkip('assessmentStudentsList', 0))
    navigate('/dashboard/data-entry/assessments')
  }

  useEffect(() => {
    if (mode === 'editing') {
      ;(getFullAssessmentData(assessmentId), {})
    }
  }, [mode])
  return (
    <Module>
      <Module.Header
        moduleName={currentModule}
        title='Assessment Builder'
        titleLink={'data-entry/assessments/add'}
      />
      <Module.Body>
        <Module.BodyHeader py='4' px='4' className='border-b border-b-2 border-primary relative '>
          <Box className='flex w-full items-center gap-2 pr-2'>
            <Box
              style={{ width: '10%' }}
              className='flex items-center justify-center gap-2 min-w-120px max-w-120px'
            >
              {loading ? (
                <Typography className='flex flex-col gap-2 items-end   justify-center'>
                  <Skeleton width={'100px'} />
                  <Skeleton width={'50px'} />
                </Typography>
              ) : assessmentType === ASSESSMENT_TYPE_STANDARD ? (
                <Typography color={'primary'} className='text-center'>
                  Standard assessment
                </Typography>
              ) : (
                <Typography color={'primary'} className='text-right'>
                  Custom assessment
                </Typography>
              )}
            </Box>
            <Box style={{ width: '20%' }}>
              <AutocompleteFieldTemplateAssessment
                id='generate-assessment-template'
                disabled={loading || mode === 'editing'}
                name='template'
                placeholder='Template*'
                value={selectedTemplate || null}
                error={error?.template ? true : false}
                helperText={error?.template}
                onChange={(value) => onChangeTemplate(value)}
                label={'Template*'}
              />
            </Box>
            {assessmentType === ASSESSMENT_TYPE_STANDARD && (
              <SelectClasses
                error={error}
                open={open}
                setOpen={setOpen}
                checkedStudentsIds={checkedStudentsIds}
                onChangeAlert={onChangeAlert}
              />
            )}

            <Box style={{ width: '20%' }}>
              <TextField
                id='generate-assessment-name'
                name='name'
                value={name ?? ''}
                error={error?.name || assessmentNameTaken ? true : false}
                helperText={error?.name || assessmentNameTaken}
                label='Name*'
                size='small'
                onChange={(e) => onChangeHeader('name', e.target.value)}
                InputProps={{ required: true }}
                style={{ mr: 1, width: '100%' }}
                variant='standard'
              />
            </Box>

            <Box style={{ width: '12%' }}>
              <InputDateUTC
                id='assessment-sitting-date'
                variant='standard'
                autoOk={true}
                name='sittingDate'
                value={sittingDate ?? null}
                error={error?.sittingDate ? true : false}
                helperText={error?.sittingDate}
                fullWidth
                label={'Sitting date*'}
                onChange={(value) => onChangeHeader('sittingDate', value)}
                size='small'
              />
            </Box>

            <Box style={{ width: '12%' }}>
              <InputDateUTC
                id='assessment-due-date'
                variant='standard'
                autoOk={true}
                name='dueDate'
                value={dueDate ?? null}
                error={error?.dueDate ? true : false}
                helperText={error?.dueDate}
                fullWidth
                label={'Due date*'}
                onChange={(value) => onChangeHeader('dueDate', value)}
                minDate={dayjs(new Date()).tz(timezone).toDate()}
                size='small'
                isStartOfTheDay={false}
              />
            </Box>
            <Box className='flex items-center pl-3 ml-1 border-l border-gray-200'>
              <RoundsBuilder disabled={loading || !template} />
            </Box>
            {assessmentType === ASSESSMENT_TYPE_CUSTOM && (
              <GlobalReviewers
                error={error}
                open={open}
                setOpen={setOpen}
                checkedStudentsIds={checkedStudentsIds}
                onChangeAlert={onChangeAlert}
              />
            )}
            {assessmentType === ASSESSMENT_TYPE_CUSTOM && (
              <Box className='flex items-center gap-1'>
                <Typography variant='inherit' className='font-bold' color='primary' component='p'>
                  Participants:
                </Typography>
                <FormControlLabel
                  control={
                    <Radio
                      checked={participantRoles.includes('Student') && !participantRoles.includes('Teacher')}
                      onChange={() => onChangeParticipantType(['Student'])}
                      color='primary'
                      disabled={loading || isEditing}
                      size='small'
                    />
                  }
                  label='Students'
                />
                <FormControlLabel
                  control={
                    <Radio
                      checked={participantRoles.includes('Teacher')}
                      onChange={() => onChangeParticipantType(['Teacher'])}
                      color='primary'
                      disabled={loading || isEditing}
                      size='small'
                    />
                  }
                  label='Teachers'
                />
              </Box>
            )}
          </Box>
          <Box className='flex items-center gap-2 justify-end'>
            <AlertSaveButton
              submodule='Surveys'
              icon={['fal', 'times']}
              title='Cancel'
              color='primary'
              variant='outlined'
              disabled={loading}
              className='shadow-sm'
              onDontSave={onCancel}
            />
            {permissions?.create && (
              <ButtonStartIcon
                startIcon={['fal', 'check']}
                onClick={handleOnSaveAssessment}
                disabled={loading}
                loading={loadingSave}
                ref={saveRef}
                color='primary'
              >
                Save
              </ButtonStartIcon>
            )}
          </Box>
        </Module.BodyHeader>
        <Module.BodyContainer>
          <TableStudents
            key={assessmentId || 'new'}
            onChecked={onChecked}
            error={error?.students}
            assessmentType={assessmentType}
          />
        </Module.BodyContainer>
      </Module.Body>
      <DialogConfirmCancel
        id={`confirm-update-assessment-modal`}
        open={openConfirmModal}
        onCancel={() => setOpenConfirmModal(false)}
        onConfirm={onConfirmUpdate}
        loading={loadingSave}
        title={`Confirm update`}
        onCloseHeader={true}
        maxWidth='xs'
      >
        <Typography component={'div'}>
          Some of the students you’ve removed from the assessment have already been assessed.
          <b> If you continue, their assessment data will be lost</b>. Are you sure you want to proceed?
        </Typography>
      </DialogConfirmCancel>
      {/* Confirm no reviewers */}
      <DialogConfirmCancel
        id={`confirm-no-reviewers-modal`}
        open={openConfirmNoReviewersModal}
        onCancel={() => setOpenConfirmNoReviewersModal(false)}
        onConfirm={saveChanges}
        loading={loadingSave}
        title={`Missing reviewer`}
        onCloseHeader={true}
        maxWidth='xs'
      >
        <Typography component={'div'}>{noReviewersMessage}</Typography>
        {loadingSave && (
          <Typography component={'div'} className='mt-2 opacity-75'>
            Saving the assessment… This can take a moment for large assessments, please wait.
          </Typography>
        )}
      </DialogConfirmCancel>
    </Module>
  )
}

export default AssessmentBuilder
